import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  NodeEntity,
  OptionalArrayField,
  ReferenceField,
} from '@uncinc/react-drupal-core/types';
import { Container, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import { SearchMoreLikeThis } from '@uncinc/react-searchkit';
import type { SearchMoreLikeThisQuery, SearchMoreLikeThisQueryFilter } from '@uncinc/react-searchkit/types';

import {
  APP_OPTIONS,
  DEFAULT_LANGUAGE,
  SEARCHKIT_HOST_URL,
  SCREEN_NORMAL,
  SCREEN_WIDE,
} from '../../config/constants';

import ButtonWrapper from '../ButtonWrapper';
import Results from './MoreLikeThisResults';

import {
  TaxonomyLabel,
  TaxonomyTheme,
} from '../../types/uncinc_profile/taxonomy';

import './style.scss';

const CONTENT_TYPE_ARTICLE = 'article';
const CONTENT_TYPE_PROJECT = 'project';
const ARTICLE_TYPE_INSIGHT_ID = 134;

const baseResultAttributes = [
  'type',
  'created',
  'page_title',
  'body_summarized',
  'body',
  'url',
  'label_name',
  'langcode',
];

const resultAttributesArticle = [
  ...baseResultAttributes,
  'article_type_name',
  'ratio_3_4_xs',
  'ratio_3_4_sm',
  'ratio_3_4_md',
  'ratio_2_3_lg',
  'ratio_3_4_lg',
  'ratio_1_1_xs',
  'ratio_1_1_sm',
];

const resultAttributesProject = [
  ...baseResultAttributes,
  'partner_title',
  'partner_svg_source_color',
  'partner_svg_source_white',
  'color_primary',
  'color_secondary',
  'project_ratio_2_3_xs',
  'project_ratio_2_3_sm',
  'project_ratio_2_3_md',
  'project_ratio_2_3_lg',
];

interface MoreLikeThisProps {
  labels?: OptionalArrayField<ReferenceField<TaxonomyLabel>[]>;
  themes?: OptionalArrayField<ReferenceField<TaxonomyTheme>[]>;
  entity: NodeEntity;
}

const MoreLikeThis: React.FC<MoreLikeThisProps> = ({
  labels = [],
  themes = [],
  entity,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const screenIsTablet = width >= SCREEN_NORMAL && width < SCREEN_WIDE;

  const entityType = entity.type[0].target_id;
  const langCode = entity.langcode[0]?.value || DEFAULT_LANGUAGE;
  const hitsPerPage = (entityType === CONTENT_TYPE_ARTICLE || screenIsTablet) ? 2 : 3;
  const requiredFilters = [];

  if (entityType === CONTENT_TYPE_ARTICLE) {
    const date = new Date();
    date.setMonth(date.getMonth() - APP_OPTIONS.parameters.relatedArticlesMaxAgeMonths);
    const filterMaxAgeSeconds = Math.floor(date.getTime() / 1000);

    requiredFilters.push(
      {
        term: {
          article_type_id: {
            value: ARTICLE_TYPE_INSIGHT_ID,
          },
        },
      },
      {
        range: {
          created: {
            gte: filterMaxAgeSeconds,
          },
        },
      },
    );
  }

  if (entityType === CONTENT_TYPE_PROJECT) {
    requiredFilters.push({
      term: {
        promoted_to_frontpage: {
          value: true,
        },
      },
    });
  }

  const filters = [
    {
      term: {
        type: {
          value: entityType,
        },
      },
    },
    {
      term: {
        langcode: {
          value: langCode,
        },
      },
    },
    ...requiredFilters,
  ];

  const query: SearchMoreLikeThisQuery = {
    fields: [
      'page_title',
      'body_summarized',
      'paragraphs_entity_title',
      'paragraphs_entity_body',
    ],
    like: [
      {
        _id: `entity:node/${entity.nid[0].value}:${langCode}`,
      },
    ],
    min_term_freq: 1,
    max_query_terms: 12,
  };

  const labelsQuery = labels.map((label) => ({
    term: {
      label_name: {
        value: label.entity.name[0].value,
      },
    },
  }));

  const themesQuery = themes.map((theme) => ({
    term: {
      theme_name: {
        value: theme.entity.name[0].value,
      },
    },
  }));

  const filter: SearchMoreLikeThisQueryFilter = {
    should: [
      ...labelsQuery,
      ...themesQuery,
    ],
    filter: filters,
  };

  return (
    <div className="MoreLikeThis">
      <Container>
        <div className="row">
          <div className="col-12">
            <SearchMoreLikeThis
              query={query}
              filter={filter}
              connection={{ host: SEARCHKIT_HOST_URL }}
              hitsPerPage={hitsPerPage}
              indexName={APP_OPTIONS.parameters.searchkitIndexAllContent}
              searchSettings={{
                search_attributes: ['page_title', 'body_summarized', 'body', 'paragraphs_entity_title', 'paragraphs_entity_body'],
                result_attributes:
                  entityType === CONTENT_TYPE_ARTICLE
                    ? resultAttributesArticle
                    : resultAttributesProject,
              }}
            >
              <Results
                type={entityType}
                headerElement={(
                  <div className="MoreLikeThis__inner">
                    <div className="row">
                      <div className="col-12 col-md-8">
                        <div className="MoreLikeThis__title">
                          <h2>
                            {entityType === CONTENT_TYPE_ARTICLE ? (
                              t('content_types.article.title')
                            ) : (
                              t('content_types.project.title')
                            )}
                          </h2>
                        </div>

                        {entityType === CONTENT_TYPE_ARTICLE && (
                          <div className="MoreLikeThis__body">
                            {t('content_types.article.body')}
                          </div>
                        )}
                      </div>
                      {entityType === CONTENT_TYPE_ARTICLE && (
                        <div className="col-12 col-md-4">
                          <ButtonWrapper href={langCode === DEFAULT_LANGUAGE ? `/${langCode}/inzichten` : `/${langCode}/insights`}>
                            {t('content_types.article.overviewButtonText')}
                          </ButtonWrapper>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                emptyState={<div />}
              />
            </SearchMoreLikeThis>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default MoreLikeThis;
